import { DisplayDateRange } from 'components/Charting/types';
import { FOUR_HOURS_MINUTES } from 'constants/optimisation-chart';
import {
  addDays,
  addHours,
  differenceInHours,
  differenceInMinutes,
  getHours,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  startOfDay,
  subDays,
} from 'date-fns';
import { toDate, toZonedTime } from 'date-fns-tz';
import {
  AllTradeMarkets,
  IChangeOverType,
  MarketKey,
} from 'pages/Optimisation/components/ContractedVolume/types';

const UK_TIMEZONE = 'Europe/London';

const UTILISATION_MARKETS = [
  'positiveQrUtilisation',
  'negativeQrUtilisation',
  'positiveSrUtilisation',
  'negativeSrUtilisation',
] as const satisfies readonly AllTradeMarkets[];

const UTILISATION_MARKETS_SET = new Set<string>(UTILISATION_MARKETS);

const SP_ANCILLARY_MARKETS = [
  'positiveBr',
  'negativeBr',
  'positiveQr',
  'negativeQr',
  'positiveSr',
  'negativeSr',
] as const satisfies readonly AllTradeMarkets[];

const SP_ANCILLARY_MARKETS_SET = new Set<string>(SP_ANCILLARY_MARKETS);

function getUKTimeDate(date: Date): Date {
  const UKDate = toZonedTime(date, UK_TIMEZONE);

  // Set the time to 23:00:00.000
  let UKDateTime = setHours(UKDate, 23);
  UKDateTime = setMinutes(UKDateTime, 0);
  UKDateTime = setSeconds(UKDateTime, 0);
  UKDateTime = setMilliseconds(UKDateTime, 0);

  return UKDateTime;
}

export const startOfTradingDay = (date: Date) => {
  const UKDate = toDate(date, { timeZone: UK_TIMEZONE });
  if (getHours(UKDate) >= 23) {
    return getUKTimeDate(UKDate);
  }
  return getUKTimeDate(subDays(UKDate, 1));
};

export const endOfTradingDay = (date: Date) => {
  const UKDate = toDate(date, { timeZone: UK_TIMEZONE });
  if (getHours(UKDate) >= 23) {
    return getUKTimeDate(addDays(UKDate, 1));
  }
  return getUKTimeDate(UKDate);
};

export const getTradingDayRange = (
  currentDate: Date,
): {
  dateRange: DisplayDateRange;
  isChangeOverDay: boolean;
  changeOverType: IChangeOverType;
} => {
  const start = startOfTradingDay(currentDate);
  const end = endOfTradingDay(currentDate);
  const diff = differenceInHours(end, start);

  const isChangeOverDay = differenceInHours(end, start) !== 24;
  const changeOverType = diff < 24 ? 'BST' : 'GMT';

  return {
    dateRange: {
      start,
      end,
    },
    isChangeOverDay,
    changeOverType,
  };
};

export function getSpNumber(date: Date): number {
  const zonedDate = toZonedTime(date, UK_TIMEZONE);
  const dayStart = toZonedTime(startOfDay(date), UK_TIMEZONE);
  const minutesSinceStartOfDay = differenceInMinutes(zonedDate, dayStart);
  return Math.floor(minutesSinceStartOfDay / 30) + 1;
}

const getTradingPeriodNumber = (
  marketKey: MarketKey,
  periodStartTime: Date,
  domainStartTime: Date,
  domainEndTime: Date,
) => {
  if (marketKey !== MarketKey.Ancillary) {
    return getSpNumber(periodStartTime);
  }
  const hoursInDay = differenceInHours(domainEndTime, domainStartTime);
  const offset = hoursInDay - 24;
  const firstSegmentEnd = addHours(domainStartTime, 4 + offset);
  let periodNumber;

  if (periodStartTime >= domainStartTime && periodStartTime < firstSegmentEnd) {
    periodNumber = 1;
  } else {
    const fourHourPeriodsCount = Math.floor(
      differenceInMinutes(periodStartTime, firstSegmentEnd) /
        FOUR_HOURS_MINUTES,
    );

    // Add 1 for the initial segment
    periodNumber = fourHourPeriodsCount + 2;
  }

  return periodNumber;
};

export const isUtilisationMarket = (market: AllTradeMarkets | undefined) =>
  !!market && UTILISATION_MARKETS_SET.has(market);

export const getContractedPeriodAttributes = (
  marketKey: MarketKey,
  periodStartTime: Date,
  domainStartTime: Date,
  domainEndTime: Date,
  market?: AllTradeMarkets,
) => {
  if (marketKey === MarketKey.BalancingMechanism) {
    return null;
  }

  if (isUtilisationMarket(market)) {
    return null;
  }

  if (market && SP_ANCILLARY_MARKETS_SET.has(market)) {
    const periodNumber = getSpNumber(periodStartTime);
    return { periodTitle: 'SP', periodNumber };
  }

  const periodTitle = marketKey === MarketKey.Ancillary ? 'EFA' : 'SP';

  const periodNumber = getTradingPeriodNumber(
    marketKey,
    periodStartTime,
    domainStartTime,
    domainEndTime,
  );

  return { periodTitle, periodNumber };
};

export const getContractedPeriodTitle = (
  marketKey: MarketKey,
  periodStartTime: Date,
  domainStartTime: Date,
  domainEndTime: Date,
  market?: AllTradeMarkets,
) => {
  const attributes = getContractedPeriodAttributes(
    marketKey,
    periodStartTime,
    domainStartTime,
    domainEndTime,
    market,
  );
  if (attributes === null) {
    return '';
  }

  const { periodTitle, periodNumber } = attributes;

  return `${periodTitle} ${periodNumber}`;
};
